/**
 * Decyzon brand logo — SINGLE ENTRY POINT for every in-app logo render.
 *
 *   import { DecyzonLogo } from "@/components/brand/DecyzonLogo";
 *
 *   <DecyzonLogo variant="wordmark" context="header" />
 *   <DecyzonLogo variant="mark"     context="footer" />
 *   <DecyzonLogo variant="lockup"   context="auth" />
 *
 * Hard rules (enforced by lint + this file):
 *  - No `size`, `width`, `style.transform`, or page-level sizing overrides.
 *  - `className` is layout-only (margin / opacity / color via currentColor).
 *  - All sizing is locked per `context`. Adding a new size = add a new context.
 *  - Geometry is path-pinned. Wordmark uses textLength + lengthAdjust so font
 *    side-bearings cannot reintroduce optical drift across devices.
 */

export type LogoVariant = "wordmark" | "mark" | "lockup";
export type LogoContext = "header" | "auth" | "footer" | "modal";

export interface DecyzonLogoProps {
  variant?: LogoVariant;
  context?: LogoContext;
  /** Layout-only utility classes (margin/opacity). NOT for sizing. */
  className?: string;
}

// ────────────────────────────────────────────────────────────────────────────
// Locked sizing tables. Page code may NOT override these.
// Wordmark + lockup are width-led; mark is height-led.
// ────────────────────────────────────────────────────────────────────────────
const WORDMARK_WIDTH: Record<LogoContext, string> = {
  // Mobile ≈ 110–130px, desktop ≈ 140–180px (per spec).
  header: "w-[120px] sm:w-[150px] md:w-[170px]",
  auth:   "w-[160px] sm:w-[200px] md:w-[230px]",
  footer: "w-[110px] sm:w-[125px]",
  modal:  "w-[130px] sm:w-[160px] md:w-[180px]",
};

const LOCKUP_HEIGHT: Record<LogoContext, string> = {
  header: "h-12",
  auth:   "h-16",
  footer: "h-10",
  modal:  "h-14",
};

const MARK_HEIGHT: Record<LogoContext, string> = {
  header: "h-6",
  auth:   "h-10",
  footer: "h-4",
  modal:  "h-7",
};

// ────────────────────────────────────────────────────────────────────────────

export function DecyzonLogo({
  variant = "wordmark",
  context = "header",
  className = "",
}: DecyzonLogoProps) {
  const baseWrap = "inline-flex items-center justify-center shrink-0 select-none";

  if (variant === "mark") {
    return (
      <span
        className={`${baseWrap} ${MARK_HEIGHT[context]} ${className}`}
        aria-label="Decyzon"
        role="img"
      >
        <svg
          viewBox="0 0 56 56"
          preserveAspectRatio="xMidYMid meet"
          xmlns="http://www.w3.org/2000/svg"
          className="h-full w-auto block"
          aria-hidden="true"
        >
          <path d="M18 10 H10 V46 H18" stroke="currentColor" strokeWidth={6} strokeLinecap="square" strokeLinejoin="miter" fill="none" />
          <path d="M38 10 H46 V46 H38" stroke="currentColor" strokeWidth={6} strokeLinecap="square" strokeLinejoin="miter" fill="none" />
          <circle cx={28} cy={28} r={6.5} fill="#1F6FEB" />
        </svg>
      </span>
    );
  }

  if (variant === "lockup") {
    // Vertical stack: mark above wordmark, both centered on a true vertical axis.
    return (
      <span
        className={`${baseWrap} ${LOCKUP_HEIGHT[context]} ${className}`}
        aria-label="Decyzon"
        role="img"
      >
        <svg
          viewBox="0 0 120 132"
          preserveAspectRatio="xMidYMid meet"
          xmlns="http://www.w3.org/2000/svg"
          className="h-full w-auto block"
          aria-hidden="true"
        >
          <g>
            <path d="M52 18 H44 V54 H52" stroke="currentColor" strokeWidth={6} strokeLinecap="square" strokeLinejoin="miter" fill="none" />
            <path d="M72 18 H80 V54 H72" stroke="currentColor" strokeWidth={6} strokeLinecap="square" strokeLinejoin="miter" fill="none" />
            <circle cx={62} cy={36} r={6.5} fill="#1F6FEB" />
          </g>
          <text
            x={60} y={108} textAnchor="middle"
            fontFamily="'Space Grotesk', 'Inter', system-ui, sans-serif"
            fontWeight={700} fontSize={22} letterSpacing={1.4}
            textLength={92} lengthAdjust="spacingAndGlyphs"
            fill="currentColor"
          >
            DECYZON
          </text>
        </svg>
      </span>
    );
  }

  // ── wordmark ──────────────────────────────────────────────────────────────
  // viewBox 226x48, equal 8px horizontal / 6px vertical padding.
  // textLength=176 + lengthAdjust pins the wordmark width regardless of font load.
  return (
    <span
      className={`${baseWrap} ${WORDMARK_WIDTH[context]} h-auto ${className}`}
      aria-label="Decyzon"
      role="img"
    >
      <svg
        viewBox="0 0 226 48"
        preserveAspectRatio="xMidYMid meet"
        xmlns="http://www.w3.org/2000/svg"
        className="w-full h-auto block"
        aria-hidden="true"
      >
        <g>
          <path d="M16 8 H8 V40 H16" stroke="currentColor" strokeWidth={4} strokeLinecap="square" strokeLinejoin="miter" fill="none" />
          <path d="M22 8 H30 V40 H22" stroke="currentColor" strokeWidth={4} strokeLinecap="square" strokeLinejoin="miter" fill="none" />
          <circle cx={19} cy={24} r={4.5} fill="#1F6FEB" />
        </g>
        <text
          x={42}
          y={32}
          textLength={176}
          lengthAdjust="spacingAndGlyphs"
          fontFamily="'Space Grotesk', 'Inter', system-ui, sans-serif"
          fontWeight={700}
          fontSize={26}
          letterSpacing={1}
          fill="currentColor"
        >
          DECYZON
        </text>
      </svg>
    </span>
  );
}

// Default export retained for backwards compatibility with existing imports
// during migration. New code MUST use the named export.
export default DecyzonLogo;
